/**
 * Refactored InspeccionDialog - Uses modular components and custom hook
 * Original: 1375 lines -> Refactored: ~150 lines
 * Backup saved at: InspeccionDialog.original.js
 */
import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Import modular components
import {
  useInspeccion,
  usePrintInspeccion,
  InspeccionToolbar,
  RequerimientoInfo,
  TestigoForm,
  DatosInspeccionForm,
  SolicitudesList,
  ProgramacionCard,
  ComentarioSection
} from './Inspeccion';

const InspeccionDialog = ({ requerimiento, open, onClose, onUpdate, readOnly = false }) => {
  // Use custom hook for all state and logic
  const {
    loading,
    sendingEmail,
    inspeccion,
    solicitudes,
    platformConfig,
    actaInputRef,
    testigoTipo,
    testigo,
    datosInspeccion,
    programacion,
    comentario,
    editingSolicitudIndex,
    catalogos,
    todasNoProcede,
    setTestigo,
    setDatosInspeccion,
    setProgramacion,
    setComentario,
    setEditingSolicitudIndex,
    handleTestigoTipoChange,
    handleSave,
    handleAddSolicitud,
    handleDeleteSolicitud,
    handleActaUpload,
    handleActaDelete,
    handleSendEmail,
    handleSendWhatsApp,
    handleTerminar,
    handleReactivar,
    updateSolicitud,
    fetchUltimoTestigo,
    magicLoading,
    sendingWhatsapp,
    canSave,
    isFormComplete,
    canSendOrPrint,
    canTerminar,
    formDirty,
    sealData,
    sealLoading
  } = useInspeccion(requerimiento, open);

  const { user } = useAuth();
  const isAdmin = user?.rol_nombre === 'Administrador';

  // Print hook
  const { handlePrint } = usePrintInspeccion();

  // Wrapper handlers that pass callbacks
  const onSave = () => handleSave(onUpdate);
  const onTerminar = () => handleTerminar(onUpdate, onClose);
  const onReactivar = () => handleReactivar(onUpdate, onClose);
  const onActaUpload = (e) => handleActaUpload(e, onSave);
  const onPrint = () => {
    if (sealLoading) return;
    handlePrint(
      requerimiento,
      testigo,
      datosInspeccion,
      programacion,
      comentario,
      solicitudes,
      platformConfig,
      sealData
    );
  };

  // Check if acta has been sent/attached for approval (locks editing)
  const isActaEnviada = !!(inspeccion?.acta_url) || ['enviado_correo', 'aprobado_correo', 'rechazado_correo', 'enviado_whatsapp', 'aprobado_whatsapp', 'rechazado_whatsapp'].includes(datosInspeccion?.actaEstado) || inspeccion?.estado === 'completada';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent 
        className="max-w-5xl w-[95vw] max-h-[95vh] flex flex-col p-0 overflow-hidden [&>button.absolute]:hidden"
        data-testid="inspeccion-dialog"
        preventMobileClose
      >
        {/* Sticky Header with Title + Toolbar */}
        <InspeccionToolbar
          loading={loading}
          sendingEmail={sendingEmail}
          sendingWhatsapp={sendingWhatsapp}
          inspeccion={inspeccion}
          isFormComplete={canSave()}
          canSendOrPrint={canSendOrPrint()}
          canTerminar={canTerminar()}
          datosInspeccion={datosInspeccion}
          formDirty={formDirty}
          onSave={onSave}
          onSendEmail={() => handleSendEmail(onUpdate)}
          onSendWhatsApp={() => handleSendWhatsApp(onUpdate)}
          onPrint={onPrint}
          sealLoading={sealLoading}
          onTerminar={onTerminar}
          onAddSolicitud={readOnly || isActaEnviada ? undefined : handleAddSolicitud}
          onClose={onClose}
          platformConfig={platformConfig}
          codigoRequerimiento={requerimiento?.codigo}
          readOnly={readOnly}
          isAdmin={isAdmin}
          requerimientoEstado={requerimiento?.estado}
          todasNoProcede={todasNoProcede}
          onReactivar={onReactivar}
        />

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* 1. Información del Requerimiento */}
          <RequerimientoInfo requerimiento={requerimiento} />

          {/* 2. Datos del Testigo */}
          <TestigoForm
            testigoTipo={testigoTipo}
            testigo={testigo}
            onTipoChange={handleTestigoTipoChange}
            onTestigoChange={setTestigo}
            readOnly={readOnly}
            onMagicClick={fetchUltimoTestigo}
            magicLoading={magicLoading}
            magicTooltip="Copiar últimos datos: Testigo, Programación y Comentario"
          />

          {/* 3. Datos de la Inspección */}
          <DatosInspeccionForm
            datosInspeccion={datosInspeccion}
            onDatosChange={setDatosInspeccion}
            onActaUpload={onActaUpload}
            onActaDelete={handleActaDelete}
            actaInputRef={actaInputRef}
            inspeccionId={inspeccion?.id}
            readOnly={readOnly}
            loading={loading}
            sendingEmail={sendingEmail}
          />

          {/* 4. Lista de Solicitudes */}
          <SolicitudesList
            solicitudes={solicitudes}
            editingIndex={editingSolicitudIndex}
            onUpdateSolicitud={updateSolicitud}
            onAdd={handleAddSolicitud}
            onDelete={handleDeleteSolicitud}
            onSetEditing={setEditingSolicitudIndex}
            readOnly={readOnly}
            isActaEnviada={isActaEnviada}
            catalogos={catalogos}
            requerimientoId={requerimiento?.id}
            canSendOrPrint={canSendOrPrint()}
            programacion={programacion}
          />

          {/* 5. Programación de Trabajos */}
          <ProgramacionCard
            programacion={programacion}
            onChange={setProgramacion}
            readOnly={readOnly}
            todasNoProcede={todasNoProcede}
          />

          {/* 6. Comentario Adicional */}
          <ComentarioSection
            comentario={comentario}
            onChange={setComentario}
            readOnly={readOnly}
          />

          {/* Validation Alert */}
          {!readOnly && !isFormComplete() && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle size={18} className="text-yellow-600" />
              <p className="text-sm text-yellow-700">
                Complete todos los campos obligatorios (*) para habilitar las acciones de envío y finalización.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InspeccionDialog;
